/**
 * The provider app's one call surface, mounted for the whole session.
 *
 * It sits in `GlobalOverlays`, BESIDE `OverlayHost` rather than inside the
 * overlay registry, and the difference is not filing. An overlay is a stack
 * entry that a route opens and closes; a call outlives every route the
 * clinician walks through while it runs, and the registry's props are plain
 * data by design — it has nowhere to put a live session. Putting the call in
 * the stack would also let a second overlay open above it and then dismiss the
 * call with it.
 *
 * It renders NOTHING until the store has an active call, so the cost of it
 * being always-mounted is one selector read.
 *
 * HOW A ROOM IS ENTERED: something records it in the call store, and this host
 * sees it and takes the session from there. There are three such somethings —
 * `JoinVisitButton` for a visit, `ringColleague` for a call placed, the ring's
 * Answer button for a call received — and every one of them goes through the
 * store rather than around it. Which room is active has one opinion.
 *
 * WHICH ROOM IS WHICH is read off the key's scope, not guessed: one store holds
 * every room, and a host that assumed a key was a visit's would ask the server
 * for a visit credential for a call.
 */
import { getTRPCClient } from "@repo/api";
import type { JoinCredential } from "@repo/conferencing";
import {
  type ActiveCall,
  Call,
  ConferencingProvider,
  isRingingOut,
  useActiveCall,
  useCall,
  type WaitingEntry,
} from "@repo/conferencing/components";
import { admitVisit, useVisits } from "@repo/data-store";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { callAnswerStore, useCallAnswered } from "./call-answer-store";
import { shouldAbandonCall } from "./call-leave";
import { callIdFromRoomKey, closeCallRoom } from "./call-room";
import { playCallLeave } from "./call-sounds";
import { closeVisitRoom, visitIdFromRoomKey } from "./visit-room";

/**
 * The element the expanded surface is measured from — the shell's content
 * region, so the call covers the chart and NOT the sidebar, header or the
 * clinician's way out of it. Absent from the DOM, the geometry falls back to
 * the whole viewport, which is exactly the swallowing this avoids; both shells
 * carry the id.
 */
export const CALL_ANCHOR_ID = "call-anchor";

export function ConferencingHost() {
  const active = useActiveCall();

  // The app only ever hosts. A guest credential belongs to the patient portal,
  // and rendering one here would ask the server for a token it will refuse.
  if (!active || active.role !== "host") return null;

  // Keyed by the room in both branches: a different room is a different
  // session, and remounting is the honest way to get one rather than swapping a
  // credential underneath a live effect.
  const visitId = visitIdFromRoomKey(active.key);
  if (visitId) return <VisitCall key={active.key} active={active} visitId={visitId} />;

  const callId = callIdFromRoomKey(active.key);
  if (callId) return <ColleagueCall key={active.key} active={active} callId={callId} />;

  return null;
}

/**
 * A call between two clinicians. The differences from a visit are all
 * absences: no waiting room, because there is nobody to admit — both parties
 * are hosts and the server mints for whoever is party to the call.
 *
 * `join` IS THE CREDENTIAL CALL FOR BOTH SIDES, and it is not `answer`.
 * `answer` belongs to the colleague being rung — only they may use it — so the
 * clinician who PLACED the call would be refused by it and could not get into
 * the room they are already sitting in. `join` mints for either party and moves
 * the ring not at all, which also means a reload does not tell the other end
 * somebody picked up.
 *
 * THE TITLE IS THE OTHER PARTY, NAMED BY THE SERVER. Nothing here could work it
 * out: the room's key is the call's id and the call store holds intent only, so
 * a client wanting a name would have to resolve a Practitioner reference itself
 * — a second opinion about identity in the one part of this feature that has
 * none. The server resolves it relative to the acting session, so each side is
 * told the other. When there is no name to be had, the wording falls back to
 * the room's own.
 */
function ColleagueCall({ active, callId }: { active: ActiveCall; callId: string }) {
  const { data: joined = null, isError } = useQuery({
    queryKey: ["colleague-call", callId],
    queryFn: async (): Promise<{ credential: JoinCredential; otherPartyName?: string }> => {
      const result = (await getTRPCClient().colleagueCall.join.mutate({ callId })) as {
        data: { credential: JoinCredential; otherPartyName?: string };
      };
      return result.data;
    },
    // Referential stability, as above: the credential is the session effect's
    // only dependency and a refetch would rebuild the session mid-call.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * A refused credential closes the surface, for the visit path's reason: with
   * `retry` off nothing further will arrive, so the alternative is a call
   * sitting on "Connecting…" forever looking like it is about to work. The
   * commonest refusal here is a ring that rang out between the click and the
   * request.
   */
  useEffect(() => {
    if (isError) closeCallRoom(callId);
  }, [isError, callId]);

  return (
    <ConferencingProvider
      roomKey={active.key}
      callRole="host"
      credential={joined?.credential ?? null}
      // A colleague with no name on file is not an error, and the room still
      // needs a label. "Colleague call" is what it falls back to — never an
      // empty title, and never the word `undefined`.
      title={joined?.otherPartyName ?? "Colleague call"}
      canShareScreen
      anchorId={CALL_ANCHOR_ID}
      onLeave={() => {
        // Confirm every press, including the recipient and an unanswered ring.
        playCallLeave();
        // The provider already closed the room. The answer store survives that
        // close so a live call cannot be mistaken for an unanswered ring.
        if (shouldAbandonCall(callId, callAnswerStore.get().context)) {
          void getTRPCClient().colleagueCall.hangUp.mutate({ callId });
        }
      }}
    >
      <CallChrome />
    </ConferencingProvider>
  );
}

function VisitCall({ active, visitId }: { active: ActiveCall; visitId: string }) {
  /**
   * Both statuses, because this list answers two questions with one query and
   * they diverge the instant the clinician admits somebody. `checkedIn` is who
   * is knocking; `inProgress` is who is already in the room. Asking only for
   * the first loses the visit at admit, and with it the title — the surface
   * would silently relabel itself "Virtual visit" mid-consult.
   */
  const { visits } = useVisits({ status: ["checkedIn", "inProgress"] });

  /**
   * `credential` MUST be referentially stable once it arrives — it is the
   * session effect's only dependency, and a fresh object each render would
   * tear the session down and rebuild it each render. React Query gives that
   * for free while the query neither refetches nor retries, which is what
   * every option below is for. They are correctness, not tuning.
   */
  const { data: credential = null, isError } = useQuery({
    queryKey: ["visit-room", "host", visitId],
    queryFn: async (): Promise<JoinCredential> => {
      const result = (await getTRPCClient().visit.joinRoomAsHost.mutate({ visitId })) as {
        data: { credential: JoinCredential };
      };
      return result.data.credential;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * A REFUSED CREDENTIAL CLOSES THE SURFACE. The button that opened it no longer
   * handles refusals — there is nothing to refuse at a store send — and the
   * reason reaches the clinician through the global tRPC error-link toast, as
   * it always did. What must not happen is the surface staying up: `retry` is
   * off, so nothing would ever arrive, and the call would sit on "Connecting…"
   * indefinitely looking like it was about to work.
   */
  useEffect(() => {
    if (isError) closeVisitRoom(visitId);
  }, [isError, visitId]);

  const visit = visits.find((candidate) => candidate.visitId === visitId);

  /**
   * WHEN THEY KNOCKED IS NOT RECORDED ANYWHERE. `checkedIn` is a status, not an
   * instant, and `VisitDto` carries the visit's scheduled `start`, which is a
   * different fact entirely — showing "waiting 42 minutes" because a visit was
   * booked for two o'clock would be worse than showing nothing. So the elapsed
   * counter runs from when THIS CLIENT first saw them waiting, and it therefore
   * understates: open the call ten minutes late and it starts at zero.
   *
   * A ref, not state, because it is a cache and must not re-render; writing it
   * here is idempotent, so a double render under StrictMode keeps the first
   * answer rather than resetting the clock.
   */
  const firstSeenRef = useRef(new Map<string, number>());
  const waiting = useMemo<readonly WaitingEntry[]>(() => {
    // Only the knocking are waiting. An admitted visit stays in `visits` for
    // the title and must not go on showing a waiting timer.
    if (!visit || visit.status !== "checkedIn") return [];
    const seen = firstSeenRef.current;
    let since = seen.get(visit.visitId);
    if (since === undefined) {
      since = Date.now();
      seen.set(visit.visitId, since);
    }
    return [{ id: visit.visitId, displayName: visit.patientName ?? "Patient", since }];
  }, [visit]);

  return (
    <ConferencingProvider
      roomKey={active.key}
      callRole="host"
      credential={credential}
      title={visit?.patientName ? `Visit with ${visit.patientName}` : "Virtual visit"}
      canShareScreen
      anchorId={CALL_ANCHOR_ID}
      waiting={waiting}
      /*
       * Admitting IS the visit's own `admit` rung — `checkedIn` → `inProgress`.
       * There is no separate admission record: withholding the guest token is
       * the waiting room, and `joinRoomAsGuest` refuses until the visit is
       * `inProgress`. So letting somebody in and starting the visit are one act,
       * which is why this is a one-line handler and not an orchestration.
       */
      onAdmit={async (id) => {
        // The entry's id IS the visit's — the list above is built from it — but
        // `VisitId` is branded and the entry carries a plain string, so admit
        // the DTO's own id rather than re-branding one by assertion.
        const target = visits.find((candidate) => candidate.visitId === id);
        if (target) await admitVisit({ visitId: target.visitId }).isPersisted.promise;
      }}
    >
      <CallChrome />
    </ConferencingProvider>
  );
}

/**
 * Which chrome, and ONLY which chrome. The stage is the provider's and is
 * already mounted above this; a variant that rendered its own would remount the
 * `<audio>` element on every minimise and kill remote audio while the call went
 * on looking connected.
 */
/**
 * Video is not attendance: a camera-off colleague has no tile, and departure
 * removes their tiles too. The provider latches the first remote presence for
 * this room, so neither can restore the ringing caption or hide Expand when
 * the clinician minimises a call that has already connected.
 */
function CallChrome() {
  const { geometry, hasRemoteAttendeeJoined } = useCall();
  const ringingOut = isRingingOut(geometry.minimised, hasRemoteAttendeeJoined);

  return geometry.minimised ? (
    <Call.Frame>
      {/*
       * WHO IS BEING RUNG, and no expand while they are. There is nothing to
       * expand into until somebody joins. The package expands on remote
       * attendee presence, which arrives even with their camera off; a video
       * tile can arrive late or never. Answering only changes the caption,
       * because the colleague still needs a credential and a meeting session.
       */}
      {ringingOut ? <Call.Header caption={<CallingCaption />} /> : null}
      <Call.WaitingChip />
      <Call.CompactControls />
      {ringingOut ? null : <Call.Expand />}
    </Call.Frame>
  ) : (
    <Call.Frame>
      <Call.Header>
        <Call.Minimise />
      </Call.Header>
      <Call.WaitingList />
      <Call.Controls />
    </Call.Frame>
  );
}

/** The wait, shown as motion that goes nowhere — see `--animate-text-shimmer`. */
function CallingCaption() {
  const answered = useCallAnswered();
  return (
    <p className="animate-text-shimmer truncate bg-[linear-gradient(90deg,var(--color-neutral-400)_25%,var(--color-neutral-50)_50%,var(--color-neutral-400)_75%)] bg-[length:200%_100%] bg-clip-text text-sm font-semibold text-transparent motion-reduce:animate-none">
      {answered ? "Connecting…" : "Calling…"}
    </p>
  );
}
